<template>
  <v-app>
    <appbarView v-if="!loading && showBars" />

    <v-main :class="{ 'no-appbar': !showBars }">
      <div id="app-wrapper" style="--v-layout-left: 0px; --v-layout-right: 0px;">
        <div id="app-inner" class="app-content-area">
          
          <v-fade-transition>
            <div v-if="loading" class="native-loader-overlay">
              <div class="glass-loader-card">
                <div class="pulse-container">
                  <img src="https://mvexpress.mx/images/mvsimple.png" alt="MVExpress" class="pulsing-logo" />
                  <div class="loading-bar-container">
                    <div class="loading-bar-progress"></div>
                  </div>
                  <h3 class="text-h6 font-weight-bold text-blue-darken-4 mt-6 text-center" style="line-height: 1.2; opacity: 0.9;">
                    "Calidad en cada entrega,<br>valor en cada marca"
                  </h3>
                </div>
              </div>
            </div>
          </v-fade-transition>

          <div v-show="!loading">
            <router-view v-slot="{ Component }">
              <keep-alive :include="['MarcasXProductoView', 'PeñafielView', 'TopDiezView', 'HomeView', 'ElectrolitView', 'ExpressMV']">
                <transition name="route-fade">
                  <component :is="Component" :key="route.name" />
                </transition>
              </keep-alive>
            </router-view>
          </div>

        </div>
      </div>
    </v-main>

    <BottomNavigation v-if="!loading && showBars" />

    <!-- Banner de sin conexión -->
    <v-fade-transition>
      <div v-if="!isOnline" class="offline-banner">
        <v-icon size="18" color="white" class="mr-1">mdi-wifi-off</v-icon>
        Sin conexión — los pedidos y la selección de productos están desactivados
      </div>
    </v-fade-transition>
  </v-app>
</template>

<script setup>
import { provide, watch, onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import { createCartStore } from "./stores/cart";
import { useUserStore } from "@/stores/userStore";
import { useTimeStore } from "./stores/timestore";
import { TextZoom } from "@capacitor/text-zoom";
import appbarView from "@/components/appbar/appbarView.vue";
import BottomNavigation from "@/components/submenu/BottomNavigation.vue";
import { useNetwork } from "@/composables/useNetwork";
import { allStaticImages } from "@/data/marcasData";
import { preloadImages } from "@/utils/preloadImages";

const loading = ref(true);
const timeStore = useTimeStore();
const cartStore = createCartStore();
provide("cartStore", cartStore);
const userStore = useUserStore();
const route = useRoute();
const { isOnline } = useNetwork();
provide("isOnline", isOnline);

onMounted(async () => {
  const safetyTimeout = setTimeout(() => {
    if (loading.value) loading.value = false;
  }, 5000);

  try {
    try { await TextZoom.set({ value: 1 }); } catch (err) { /* silent */ }

    // 🚀 Inicializar sesión (crítico) y sincronizar tiempo en segundo plano (no bloqueante)
    timeStore.syncServerTime(); // no await — actualiza el reloj en background
    if (!userStore.isSessionReady) {
      await userStore.initSession();
    }

    // Intervalos en segundo plano (no bloqueantes)
    setInterval(() => timeStore.tick(), 1000);
    setInterval(() => timeStore.syncServerTime(), 5 * 60 * 1000);

  } catch (e) {
    console.error("Error en inicialización:", e);
  } finally {
    clearTimeout(safetyTimeout);
    loading.value = false;
    // Precargar imágenes estáticas en background una vez que la app está lista
    setTimeout(() => preloadImages(allStaticImages), 500);
  }
});

const showBars = computed(() => {
  const hiddenRoutes = ['Login', 'Register', 'login', 'register', 'Welcome', 'LoginHome', 'BootstrapView', 'aviso-privacidad'];
  return userStore.isAuthenticated && route.name && !hiddenRoutes.includes(route.name);
});

watch(
  () => route.meta.title,
  (newTitle) => {
    document.title = newTitle || "MVExpress";
  },
  { immediate: true }
);
</script>

<style>
/* ── Transición entre vistas: solo fade-in (sin leave para evitar pantalla blanca con keep-alive) ── */
.route-fade-enter-active {
  transition: opacity 0.2s ease;
}
.route-fade-enter-from {
  opacity: 0;
}

/* ── Botón flotante de carrito (global) ── */
.floating-cart-btn {
  position: fixed;
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  right: 20px;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  z-index: 2000;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.45) !important;
  /* Respuesta táctil inmediata — elimina el delay de 300ms en móvil */
  touch-action: manipulation !important;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  cursor: pointer;
}
/* Ripple instantáneo — sin animación que bloquee la apertura del diálogo */
.floating-cart-btn .v-ripple__animation {
  animation-duration: 0ms !important;
  opacity: 0 !important;
}
.cart-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #FF6B00;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  font-size: 11px;
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  padding: 0 3px;
  pointer-events: none;
}

.offline-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background-color: #b71c1c;
  color: white;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.3);
}

/* ── Contenedor de contenido centrado ── */
.app-content-area {
  width: 100%;
}

/* ── Responsive: Desktop (≥960px) ── */
@media (min-width: 960px) {
  .app-content-area {
    max-width: 1280px;
    margin: 0 auto;
  }

  /* En desktop no hay bottom nav, reajustar padding inferior */
  .v-main {
    padding-bottom: 0 !important;
  }

  /* Botón de carrito: anclado relativo al área de contenido en pantallas grandes */
  .floating-cart-btn {
    right: max(20px, calc((100vw - 1280px) / 2 + 20px));
    bottom: 40px;
  }

  /* El banner offline no necesita safe-area en desktop */
  .offline-banner {
    padding: 10px 16px;
  }

  /* Los brand views tienen padding-bottom excesivo para iOS; en desktop normalizar */
  .main-content {
    padding-bottom: 40px !important;
    min-height: auto;
  }
}

/* Estilos globales para sensación nativa */
/* Bloquear scroll del documento — en app nativa NUNCA debe moverse el body */
html, body {
  overflow: hidden !important;
  height: 100% !important;
  width: 100% !important;
  max-width: 100vw !important;
  overscroll-behavior: none !important; /* sin rebote en el documento */
}

:root {
  --safe-area-top: env(safe-area-inset-top, 40px);
  --safe-area-bottom: env(safe-area-inset-bottom, 20px);
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.native-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #f8faff;
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.glass-loader-card {
  padding: 40px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.05);
  border: 1px solid rgba(255,255,255,0.4);
}

.pulse-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pulsing-logo {
  width: 120px;
  height: auto;
  animation: nativePulse 2s ease-in-out infinite;
}

.loading-bar-container {
  width: 160px;
  height: 4px;
  background: rgba(13, 71, 161, 0.1);
  border-radius: 10px;
  margin-top: 30px;
  overflow: hidden;
  position: relative;
}

.loading-bar-progress {
  width: 40%;
  height: 100%;
  background: #0D47A1;
  border-radius: 10px;
  position: absolute;
  animation: barSlide 1.5s ease-in-out infinite;
}

@keyframes barSlide {
  0% { left: -40%; }
  100% { left: 100%; }
}

@keyframes nativePulse {
  0% { transform: scale(0.95); filter: brightness(1); }
  50% { transform: scale(1.02); filter: brightness(1.1); }
  100% { transform: scale(0.95); filter: brightness(1); }
}

.v-app-bar {
  top: 0 !important;
  padding-top: var(--safe-area-top) !important;
  height: calc(64px + var(--safe-area-top)) !important;
  z-index: 1000 !important;
}

.v-application {
  padding-top: 0;
  padding-bottom: var(--safe-area-bottom);
}

#app-wrapper {
  padding-left: env(safe-area-inset-left, 0);
  padding-right: env(safe-area-inset-right, 0);
  box-sizing: border-box;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.v-main {
  padding-top: calc(64px + var(--safe-area-top)) !important;
}

.v-main.no-appbar {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

@media (min-width: 960px) {
  .v-main {
    padding-top: 64px !important;
  }

  .v-app-bar {
    padding-top: 0 !important;
    height: 64px !important;
  }
}

/* Optimización de scroll nativo */
* {
  -webkit-tap-highlight-color: transparent;
  outline: none;
}

.scroll-native {
  -webkit-overflow-scrolling: touch;
}

html, body, button, a, input, .v-btn, .v-list-item {
  touch-action: manipulation;
}
</style>